import { useState, useEffect } from "react";
import "./FileInput.scss";

const FileInput = ({
  onFileSelect,
  onChange, // Add support for onChange prop
  maxSize = 5000000, // Default max size 5 MB (adjusted up from 500KB)
  allowedTypes = ["image/jpeg", "image/png", "application/pdf"], // Added PDF support
  errorMessageSize = "File size exceeds the limit of 5 MB",
  errorMessageType = "Invalid file type. Only JPEG, PNG, and PDF files are allowed.",
  required = false,
  id = "",
}) => {
  const [fileName, setFileName] = useState("No file chosen");
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Function to get appropriate icon based on file type
  const getFileIcon = (fileType) => {
    if (fileType?.startsWith("image/")) {
      return "🖼️"; // Image icon
    } else if (fileType === "application/pdf") {
      return "📄"; // PDF icon
    }
    return "📎"; // Generic file icon
  };

  // Function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Create preview URL for the file
  const createPreviewUrl = (file) => {
    if (file.type.startsWith("image/")) {
      return URL.createObjectURL(file);
    }
    return null;
  };

  // Handle mouse enter for preview
  const handlePreviewMouseEnter = () => {
    if (uploadedFile) {
      setShowPreview(true);
      if (uploadedFile.type.startsWith("image/") && !previewUrl) {
        setPreviewUrl(createPreviewUrl(uploadedFile));
      }
    }
  };

  // Handle mouse leave for preview
  const handlePreviewMouseLeave = () => {
    setShowPreview(false);
  };

  // Cleanup preview URL when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    validateAndSetFile(file, event);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files[0];
    validateAndSetFile(file, event);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const validateAndSetFile = (file, originalEvent) => {
    if (file) {
      if (file.size > maxSize) {
        setError(errorMessageSize);
        setFileName("No file chosen");
        setUploadedFile(null);
        // Call callbacks with null to indicate error
        if (onFileSelect) onFileSelect(null);
        if (onChange)
          onChange({ ...originalEvent, isValid: false, errorType: "size" });
        return;
      }

      if (!allowedTypes.includes(file.type)) {
        setError(errorMessageType);
        setFileName("No file chosen");
        setUploadedFile(null);
        // Call callbacks with null to indicate error
        if (onFileSelect) onFileSelect(null);
        if (onChange)
          onChange({ ...originalEvent, isValid: false, errorType: "type" });
        return;
      }

      setError("");
      setFileName(file.name);
      setUploadedFile(file);

      // Clean up previous preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }

      // Call both callbacks if provided
      if (onFileSelect) {
        onFileSelect(file);
      }

      if (onChange) {
        // For compatibility with existing code expecting e.target.files[0]
        onChange({
          ...originalEvent,
          isValid: true,
          target: {
            ...originalEvent.target,
            files: originalEvent.target.files,
          },
        });
      }
    } else {
      setFileName("No file chosen");
      setUploadedFile(null);
      // Clean up preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      // Call callbacks with null
      if (onFileSelect) onFileSelect(null);
      if (onChange)
        onChange({ ...originalEvent, isValid: false, errorType: "noFile" });
    }
  };

  return (
    <div
      className={`file-input-container ${isDragOver ? "drag-over" : ""}`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <label className="file-upload-label">
        <input
          type="file"
          onChange={handleFileChange}
          multiple={false}
          className="file-input"
          accept={allowedTypes.join(",")}
          required={required}
          id={id}
        />
        <span className="file-name-container">
          {uploadedFile && (
            <>
              <span className="file-icon">
                {getFileIcon(uploadedFile.type)}
              </span>
              <span className="file-name">{fileName}</span>
              <button
                type="button"
                className="preview-button"
                onClick={handlePreviewMouseEnter}
                title="Preview file contents"
              >
                👁️ Preview
              </button>
            </>
          )}
          {!uploadedFile && <span className="file-name">{fileName}</span>}
        </span>
      </label>
      {error && <div className="error-message">{error}</div>}

      {/* File Preview Popup */}
      {showPreview && uploadedFile && (
        <>
          <div
            className="modal-overlay"
            onClick={handlePreviewMouseLeave}
          ></div>
          <div className="file-preview-popup">
            <div className="preview-header">
              <span className="preview-title">File Preview</span>
              <button
                className="close-button"
                onClick={handlePreviewMouseLeave}
                title="Close preview"
              >
                ✕
              </button>

              <div className="file-details">
                <span className="preview-filename">{uploadedFile.name}</span>
                <span className="preview-filesize">
                  {formatFileSize(uploadedFile.size)} • {uploadedFile.type}
                </span>
              </div>
            </div>
            <div className="preview-content">
              {uploadedFile.type.startsWith("image/") ? (
                <div className="image-preview-container">
                  {previewUrl && (
                    <img
                      src={previewUrl}
                      alt={uploadedFile.name}
                      className="preview-image"
                    />
                  )}
                  <div className="image-info">
                    <p>✅ Image ready for upload</p>
                  </div>
                </div>
              ) : uploadedFile.type === "application/pdf" ? (
                <div className="pdf-preview">
                  <iframe
                    src={URL.createObjectURL(uploadedFile)}
                    title="PDF Preview"
                    width="100%"
                    height="400px"
                    style={{ border: "none", borderRadius: "8px" }}
                  ></iframe>
                  <div className="pdf-info">
                    <p>
                      <strong>PDF Document</strong>
                    </p>
                    <p>✅ Preview rendered</p>
                  </div>
                </div>
              ) : (
                <div className="file-preview">
                  <div className="file-visual">
                    <div className="file-icon-large">📎</div>
                    <div className="file-type-badge">
                      {uploadedFile.type.split("/")[1]?.toUpperCase() || "FILE"}
                    </div>
                  </div>
                  <div className="file-info">
                    <p>
                      <strong>File ready for upload</strong>
                    </p>
                    <p>✅ {uploadedFile.type}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FileInput;
